import {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import Icon from '@mui/material/Icon';
import {DragDropContext, Draggable, Droppable} from '@hello-pangea/dnd';
import axios from 'axios';
import {Confirm, Loading, Notify} from 'notiflix';
import {useSelector} from 'react-redux';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import {ENDPOINT} from 'constants';
import {valuePackApi} from 'api/valuePackApi';
import {countryApi} from 'api/countryApi';
import {slugify} from './filterConfig';
import ValuePackPicker from './ValuePackPicker';

// Settings > Dynamic Filters > Value Packs / Products > Price Options.
// `module` prop: 'value_pack' (default) or 'product'. Product dropdowns are saved
// the same way, with the product id in valuePackIds.
// Each group is a dropdown on the value pack / product detail page (e.g. "Company size")
// whose items change the pack price by a percentage or a flat amount per month.

const newItem = () => ({
  id: '',
  label: '',
  type: 'percent',
  percent: 0,
  flatBase: 0,
  flatByCountry: {},
});

// One dropdown inside the Add / Edit page
const newDropdown = () => ({
  id: null,
  label: '',
  key: '',
  keyTouched: false,
  helpText: '',
  isRequired: true,
  items: [newItem()],
});

// The page can hold several dropdowns; "Applies to" and "Active" are shared
const EMPTY_FORM = {
  isActive: true,
  // "All value packs" is turned off for now: every dropdown is on one value pack
  scope: 'specific',
  valuePackIds: [],
  dropdowns: [newDropdown()],
  // Saved dropdowns removed on the edit page, deleted on save
  removedIds: [],
};

// A value pack shows at most this many dropdowns (also checked by the backend)
const MAX_DROPDOWNS_PER_PACK = 4;

// Same formula as the backend (utils/priceOptions.js) for one tier price:
// price × (1 + percent / 100) + flat per month × months
// `discount` = the plan discount already in `price`; the cart discounts the flat
// amount too, so it is taken off the flat part here
const priceWithOption = (price, adj, tier, countries, discount = 0) => {
  const base = Number(price) || 0;
  if (!adj || base <= 0) return base;
  const months =
    tier.durationUnit && tier.durationUnit !== 'month'
      ? (Number(tier.duration) || 30) / 30
      : Number(tier.duration) || 1;
  if (adj.type === 'flat') {
    const byCountry = adj.flatByCountry?.[tier.countryId];
    const country = countries.find(
      c => String(c.id) === String(tier.countryId),
    );
    const flat =
      byCountry !== undefined && byCountry !== ''
        ? Number(byCountry) || 0
        : (Number(adj.flatBase) || 0) * (Number(country?.priceMultiplier) || 1);
    const flatAfterDiscount =
      Math.max(0, flat) * (1 - (Number(discount) || 0) / 100);
    return (
      Math.round((base + flatAfterDiscount * Math.max(1, months)) * 100) / 100
    );
  }
  const adjusted = base * (1 + (Number(adj.percent) || 0) / 100);
  return Math.max(0, Math.round(adjusted * 100) / 100);
};

const formatMoney = (symbol, value) =>
  `${symbol || ''}${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

// Plan types of the detail page cards (subscriptionPricing keys)
const PLAN_TYPES = [
  {value: 'one_time', label: 'One Time Purchase'},
  {value: 'subscription_no_cancellation', label: 'Fixed Subscription'},
  {value: 'subscription_cancellation_allowed', label: 'Flexible Subscription'},
];

// Package durations of the tiers (the detail page "Package Duration" list)
const durationsOf = tiers => {
  const seen = new Map();
  (tiers || []).forEach(t => {
    if (!seen.has(t.durationId)) {
      seen.set(t.durationId, {
        id: t.durationId,
        name: t.durationName || `${t.duration} months`,
      });
    }
  });
  return [...seen.values()];
};

// Tiers of the picked duration (first duration when it is not in the list)
const tiersOfDuration = (tiers, durationId) => {
  if (!tiers) return null;
  const list = durationsOf(tiers);
  const id = list.some(d => d.id === durationId) ? durationId : list[0]?.id;
  return tiers.filter(t => t.durationId === id);
};

// The cards of the value pack detail page (Low / Mid / High for the country and
// plan type): original and display price without any item, and with this item
const TierPricePreview = ({tiers, adj, countries, plan}) => {
  if (tiers === null) {
    return (
      <MDTypography variant="caption" color="text" display="block" mt={1}>
        Loading prices…
      </MDTypography>
    );
  }
  if (!tiers.length) {
    return (
      <MDTypography variant="caption" color="text" display="block" mt={1}>
        No prices for this country.
      </MDTypography>
    );
  }
  const cell = {
    padding: '6px 8px',
    borderBottom: '1px solid #eee',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  };
  return (
    <MDBox mt={1} sx={{overflowX: 'auto'}}>
      <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 12}}>
        <thead>
          <tr style={{color: '#7b809a'}}>
            <th style={cell}>Card</th>
            <th style={cell}>Original price</th>
            <th style={cell}>Display price</th>
            <th style={cell}>Original price with this item</th>
            <th style={cell}>Display price with this item</th>
          </tr>
        </thead>
        <tbody>
          {tiers.map(tier => {
            // Same price the card shows: the plan type's price, else the tier price
            const option = tier.subscriptionPricing?.[plan];
            const original =
              Number(
                option?.originalPrice ??
                  (tier.originalPrice || tier.currentPrice),
              ) || 0;
            const display =
              Number(option?.finalPrice ?? tier.currentPrice) || 0;
            const months = Number(tier.duration) || 1;
            const symbol = tier.currencySymbol || tier.currency || '';
            const displayWith = priceWithOption(
              display,
              adj,
              tier,
              countries,
              option?.discount,
            );
            return (
              <tr key={tier.id}>
                <td style={cell}>
                  {tier.tierType} · {tier.durationName || `${months} months`}
                </td>
                <td style={cell}>{formatMoney(symbol, original)}</td>
                <td style={cell}>
                  {formatMoney(symbol, display)}
                  <span style={{color: '#7b809a'}}>
                    {' '}
                    ({formatMoney(symbol, Math.round(display / months))}/month)
                  </span>
                </td>
                <td style={{...cell, fontWeight: 600}}>
                  {formatMoney(
                    symbol,
                    priceWithOption(original, adj, tier, countries),
                  )}
                </td>
                <td style={{...cell, fontWeight: 600, color: '#1d5dff'}}>
                  {formatMoney(symbol, displayWith)}
                  <span style={{fontWeight: 400}}>
                    {' '}
                    ({formatMoney(symbol, Math.round(displayWith / months))}
                    /month)
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </MDBox>
  );
};

TierPricePreview.propTypes = {
  tiers: PropTypes.arrayOf(PropTypes.object),
  adj: PropTypes.object,
  countries: PropTypes.arrayOf(PropTypes.object).isRequired,
  plan: PropTypes.string,
};

TierPricePreview.defaultProps = {
  tiers: null,
  adj: null,
  plan: 'one_time',
};

// Country + plan type of the price preview, used in both dialogs
const PreviewControls = ({
  countries,
  country,
  plan,
  tiers,
  duration,
  onCountryChange,
  onPlanChange,
  onDurationChange,
}) => {
  const durations = durationsOf(tiers);
  const durationValue = durations.some(d => d.id === duration)
    ? duration
    : durations[0]?.id || '';
  return (
    <>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          fullWidth
          label="Country"
          value={country}
          onChange={e => onCountryChange(e.target.value)}
          SelectProps={{sx: {height: 44}}}>
          {(countries.length ? countries : [{countryCode: country}]).map(c => (
            <MenuItem key={c.countryCode} value={c.countryCode}>
              {c.countryName
                ? `${c.countryName} (${c.countryCode})`
                : c.countryCode}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          fullWidth
          label="Package duration"
          value={durationValue}
          disabled={!durations.length}
          onChange={e => onDurationChange(e.target.value)}
          SelectProps={{sx: {height: 44}}}>
          {durations.map(d => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          select
          fullWidth
          label="Plan type"
          value={plan}
          onChange={e => onPlanChange(e.target.value)}
          SelectProps={{sx: {height: 44}}}>
          {PLAN_TYPES.map(p => (
            <MenuItem key={p.value} value={p.value}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </>
  );
};

PreviewControls.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.object).isRequired,
  country: PropTypes.string.isRequired,
  plan: PropTypes.string.isRequired,
  tiers: PropTypes.arrayOf(PropTypes.object),
  duration: PropTypes.string,
  onCountryChange: PropTypes.func.isRequired,
  onPlanChange: PropTypes.func.isRequired,
  onDurationChange: PropTypes.func.isRequired,
};

PreviewControls.defaultProps = {
  tiers: null,
  duration: '',
};

// Accent colour per dropdown card (repeats after 4)
const DROPDOWN_ACCENTS = ['#1d5dff', '#7b3fe4', '#e91e63', '#fb8c00'];

// White card with an icon badge and a title, for the sections of the Add / Edit page
const SectionCard = ({icon, title, subtitle, accent, action, children}) => (
  <MDBox
    bgColor="white"
    borderRadius="lg"
    shadow="sm"
    p={{xs: 2, md: 3}}
    sx={{border: '1px solid #e9edf5'}}>
    <MDBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={1.5}
      mb={2.5}
      flexWrap="wrap">
      <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          width={40}
          height={40}
          borderRadius="md"
          sx={{background: `${accent}14`, color: accent}}>
          <Icon>{icon}</Icon>
        </MDBox>
        <MDBox minWidth={0}>
          <MDTypography variant="h6" fontWeight="medium" lineHeight={1.3}>
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography variant="caption" color="text" display="block">
              {subtitle}
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      {action}
    </MDBox>
    {children}
  </MDBox>
);

SectionCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node,
  accent: PropTypes.string,
  action: PropTypes.node,
  children: PropTypes.node,
};

SectionCard.defaultProps = {
  subtitle: null,
  accent: '#1d5dff',
  action: null,
  children: null,
};

// Price table under an item: collapsed by default, "Show prices" expands it
const CollapsiblePrices = props => {
  const [open, setOpen] = useState(false);
  return (
    <MDBox mt={1}>
      <MDButton
        variant="text"
        color="info"
        size="small"
        sx={{px: 0.5, minHeight: 0}}
        onClick={() => setOpen(prev => !prev)}>
        <Icon sx={{mr: 0.5}}>{open ? 'expand_less' : 'expand_more'}</Icon>
        {open ? 'Hide prices' : 'Show prices'}
      </MDButton>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <TierPricePreview {...props} />
      </Collapse>
    </MDBox>
  );
};

const describeAdjustment = adj => {
  if (!adj) return '';
  if (adj.type === 'flat') return `+ $${Number(adj.flatBase || 0)} / mo`;
  const p = Number(adj.percent || 0);
  return `${p >= 0 ? '+' : ''}${p}%`;
};

// Type + value fields of one adjustment (an item's default or a pack override)
const AdjustmentFields = ({value, onChange, countries}) => {
  const [showCountries, setShowCountries] = useState(
    Object.keys(value.flatByCountry || {}).length > 0,
  );
  const set = patch => onChange({...value, ...patch});

  return (
    <>
      <Grid item xs={6} sm={3}>
        <TextField
          select
          fullWidth
          size="small"
          label="Price change"
          value={value.type}
          onChange={e => set({type: e.target.value})}
          SelectProps={{sx: {height: 40}}}>
          <MenuItem value="percent">Percentage (%)</MenuItem>
          <MenuItem value="flat">Flat amount / month</MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={6} sm={3}>
        {value.type === 'percent' ? (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="% (use - to reduce)"
            value={value.percent}
            onChange={e => set({percent: e.target.value})}
          />
        ) : (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Base amount (USD) / month"
            value={value.flatBase}
            inputProps={{min: 0}}
            onChange={e => set({flatBase: e.target.value})}
          />
        )}
      </Grid>
      {value.type === 'flat' && (
        <Grid item xs={12}>
          <MDTypography
            variant="caption"
            color="info"
            sx={{cursor: 'pointer'}}
            onClick={() => setShowCountries(s => !s)}>
            {showCountries ? 'Hide' : 'Set'} amount per country (optional —
            otherwise the USD amount is converted with the country price
            multiplier)
          </MDTypography>
          <Collapse in={showCountries}>
            <Grid container spacing={1} mt={0.5}>
              {countries.map(c => (
                <Grid item xs={6} sm={3} key={c.id}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={`${c.countryCode} (${
                      c.currencySymbol || c.currencyCode || ''
                    })`}
                    value={value.flatByCountry?.[c.id] ?? ''}
                    inputProps={{min: 0}}
                    onChange={e =>
                      set({
                        flatByCountry: {
                          ...(value.flatByCountry || {}),
                          [c.id]: e.target.value,
                        },
                      })
                    }
                  />
                </Grid>
              ))}
            </Grid>
          </Collapse>
        </Grid>
      )}
    </>
  );
};

AdjustmentFields.propTypes = {
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.object).isRequired,
};

const PriceOptions = ({module}) => {
  const {user} = useSelector(state => state.auth);
  const authHeaders = {headers: {Authorization: `Bearer ${user?.accessToken}`}};
  const api = `${ENDPOINT}api/v1/admin/price-options`;
  const isProduct = module === 'product';
  const noun = isProduct ? 'product' : 'value pack';
  const Noun = isProduct ? 'Product' : 'Value pack';

  // Product picker / names: paged list from the price options API
  const fetchProductOptions = async params => {
    const {data} = await axios.get(`${api}/products/options`, {
      ...authHeaders,
      params,
    });
    return {
      valuePacks: data?.data?.products || [],
      hasMore: Boolean(data?.data?.hasMore),
    };
  };

  const [groups, setGroups] = useState([]);
  // Names of the value packs seen so far ({id, name}): the packs of saved
  // dropdowns plus every page the picker has loaded. Never the whole table.
  const [packs, setPacks] = useState([]);
  const rememberPacks = list =>
    setPacks(prev => {
      const known = new Set(prev.map(p => String(p.id)));
      const fresh = list.filter(p => !known.has(String(p.id)));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  const [countries, setCountries] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Per-pack prices dialog
  const [overrideGroup, setOverrideGroup] = useState(null);
  const [overridePackId, setOverridePackId] = useState('');
  const [overrides, setOverrides] = useState([]);
  const [overrideDraft, setOverrideDraft] = useState({});
  const [packTiers, setPackTiers] = useState([]);
  // Add / Edit page: pack whose tier prices are previewed under each item
  const [previewPackId, setPreviewPackId] = useState('');
  const [previewTiers, setPreviewTiers] = useState([]);
  // Country and plan type of the preview (same as the detail page cards)
  const [previewCountry, setPreviewCountry] = useState('IN');
  const [previewPlan, setPreviewPlan] = useState('one_time');
  const [previewDuration, setPreviewDuration] = useState('');

  // Tiers the detail page shows for that country (Low / Mid / High), with the
  // plan type prices, from the same public API the storefront uses
  const loadPackTiers = (packId, setTiers, countryCode = previewCountry) => {
    setTiers(packId ? null : []);
    if (!packId) return;
    if (isProduct) {
      // Package prices of the product (Low / Mid / High per duration)
      axios
        .get(`${api}/products/${packId}/prices`, {
          ...authHeaders,
          params: {countryCode},
        })
        .then(res => {
          const order = {Low: 0, Mid: 1, High: 2};
          setTiers(
            (res?.data?.data || []).sort(
              (a, b) =>
                (Number(a.duration) || 0) - (Number(b.duration) || 0) ||
                (order[a.tierType] ?? 9) - (order[b.tierType] ?? 9),
            ),
          );
        })
        .catch(() => setTiers([]));
      return;
    }
    axios
      .get(
        `${ENDPOINT}api/v1/users/valuePacks/getValuePackBySlugOrId/${packId}`,
        {headers: {'city-or-country': countryCode}},
      )
      .then(res => {
        const list = res?.data?.data?.valuePack?.tiers;
        const order = {Low: 0, Mid: 1, High: 2};
        setTiers(
          (Array.isArray(list) ? list : [])
            .filter(t => t.isActive !== false)
            .sort(
              (a, b) =>
                (Number(a.duration) || 0) - (Number(b.duration) || 0) ||
                (order[a.tierType] ?? 9) - (order[b.tierType] ?? 9),
            ),
        );
      })
      .catch(() => setTiers([]));
  };

  const changePreviewCountry = code => {
    setPreviewCountry(code);
    loadPackTiers(previewPackId, setPreviewTiers, code);
    if (overridePackId) loadPackTiers(overridePackId, setPackTiers, code);
  };

  const selectPreviewPack = packId => {
    setPreviewPackId(packId);
    loadPackTiers(packId, setPreviewTiers);
  };

  const packName = id =>
    packs.find(p => String(p.id) === String(id))?.name || Noun;

  const fetchGroups = async () => {
    try {
      Loading.circle('Loading price options...');
      const {data} = await axios.get(api, {...authHeaders, params: {module}});
      setGroups(data.data || []);
    } catch (error) {
      Notify.failure(
        error?.response?.data?.message || 'Failed to fetch price options',
      );
    } finally {
      Loading.remove();
    }
  };

  useEffect(() => {
    fetchGroups();
    countryApi
      .getActiveCountries()
      .then(res =>
        setCountries(
          Array.isArray(res) ? res : res?.countries || res?.data || [],
        ),
      )
      .catch(() => setCountries([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Names for the list rows: only the packs the saved dropdowns are on
  useEffect(() => {
    const known = new Set(packs.map(p => String(p.id)));
    const missing = [
      ...new Set(groups.flatMap(g => (g.valuePackIds || []).map(String))),
    ].filter(id => !known.has(id));
    if (!missing.length) return;
    (isProduct
      ? fetchProductOptions({ids: missing.join(',')})
      : valuePackApi.fetchValuePackOptions({ids: missing})
    )
      .then(({valuePacks}) =>
        rememberPacks(valuePacks.map(p => ({id: p.id, name: p.name}))),
      )
      .catch(() =>
        Notify.failure(
          `Could not load ${noun} names. The backend may need the latest deploy.`,
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups]);

  const setField = patch => setForm(prev => ({...prev, ...patch}));

  const openCreate = () => {
    setEditing(null);
    // Prices show once the value pack is picked
    selectPreviewPack('');
    setForm({...EMPTY_FORM, dropdowns: [newDropdown()]});
    setDialogOpen(true);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  // Edit opens the page with every dropdown of that set; more can be added
  const openEdit = set => {
    setEditing(set);
    selectPreviewPack(
      (set.scope === 'specific' && set.valuePackIds?.[0]) || '',
    );
    setForm({
      isActive: set.groups.some(g => g.isActive),
      scope: 'specific',
      // An old all-pack set has no pack yet: the admin picks one
      valuePackIds:
        set.scope === 'specific' ? (set.valuePackIds || []).slice(0, 1) : [],
      removedIds: [],
      dropdowns: set.groups.map(group => ({
        id: group.id,
        label: group.label,
        key: group.key,
        keyTouched: true,
        helpText: group.helpText || '',
        isRequired: group.isRequired,
        items: (group.items || []).map(i => ({...newItem(), ...i})),
      })),
    });
    setDialogOpen(true);
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const formPackId = form.valuePackIds[0];
  const savedOnPack = formPackId
    ? groups.filter(
        g =>
          g.isActive &&
          g.scope === 'specific' &&
          (g.valuePackIds || []).map(String).includes(String(formPackId)) &&
          !form.dropdowns.some(d => d.id === g.id) &&
          !(form.removedIds || []).some(r => r.id === g.id),
      ).length
    : 0;

  const updateDropdown = (dIndex, patch) =>
    setForm(prev => ({
      ...prev,
      dropdowns: prev.dropdowns.map((d, i) =>
        i === dIndex ? {...d, ...patch} : d,
      ),
    }));

  const updateItem = (dIndex, index, patch) =>
    setForm(prev => ({
      ...prev,
      dropdowns: prev.dropdowns.map((d, i) =>
        i === dIndex
          ? {
              ...d,
              items: d.items.map((item, j) =>
                j === index ? {...item, ...patch} : item,
              ),
            }
          : d,
      ),
    }));

  // Saves every dropdown on the page: existing ones are updated, new ones created
  const handleSave = async () => {
    if (!form.valuePackIds.length) {
      return Notify.warning(`Pick a ${noun}`);
    }
    if (form.dropdowns.length + savedOnPack > MAX_DROPDOWNS_PER_PACK) {
      return Notify.warning(
        `A ${noun} can have at most ${MAX_DROPDOWNS_PER_PACK} dropdowns. ${packName(
          form.valuePackIds[0],
        )} already has ${savedOnPack}.`,
      );
    }
    const dropdowns = form.dropdowns.map((d, i) => ({
      ...d,
      position: i + 1,
      items: d.items.filter(item => item.label.trim()),
    }));
    const keys = new Set();
    for (const d of dropdowns) {
      if (!d.label.trim())
        return Notify.warning(`Dropdown ${d.position}: label is required`);
      if (!d.items.length)
        return Notify.warning(`Dropdown ${d.position}: add at least one item`);
      const key = slugify(d.key || d.label);
      if (keys.has(key))
        return Notify.warning(`Two dropdowns have the same key "${key}"`);
      keys.add(key);
    }

    setSaving(true);
    let saved = 0;
    try {
      for (const removed of form.removedIds || []) {
        // eslint-disable-next-line no-await-in-loop
        await axios.delete(
          `${api}/${removed.id}?label=${encodeURIComponent(removed.label || '')}`,
          authHeaders,
        );
      }
      setField({removedIds: []});
      for (const [dIndex, d] of dropdowns.entries()) {
        const payload = {
          label: d.label,
          key: slugify(d.key || d.label),
          helpText: d.helpText,
          isRequired: d.isRequired,
          isActive: form.isActive,
          scope: form.scope,
          module,
          valuePackIds: form.valuePackIds,
          items: d.items,
        };
        if (d.id) {
          // eslint-disable-next-line no-await-in-loop
          await axios.put(`${api}/${d.id}`, payload, authHeaders);
        } else {
          // eslint-disable-next-line no-await-in-loop
          const {data} = await axios.post(api, payload, authHeaders);
          // Remember the new id so saving again after an error doesn't create it twice
          updateDropdown(dIndex, {id: data?.data?.id || null});
        }
        saved += 1;
      }
      Notify.success(
        saved === 1 ? 'Price option saved' : `${saved} price options saved`,
      );
      setDialogOpen(false);
    } catch (err) {
      Notify.failure(
        `${saved ? `${saved} saved, then: ` : ''}${
          err?.response?.data?.message || 'Failed to save price option'
        }`,
      );
    } finally {
      setSaving(false);
      fetchGroups();
    }
    return null;
  };

  const quickUpdate = async (set, patch) => {
    const ids = set.groups.map(g => g.id);
    const previous = groups;
    setGroups(list =>
      list.map(g => (ids.includes(g.id) ? {...g, ...patch} : g)),
    );
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await axios.put(`${api}/${id}`, patch, authHeaders);
      }
    } catch (err) {
      setGroups(previous);
      Notify.failure(err?.response?.data?.message || 'Failed to update');
      fetchGroups();
    }
  };

  const handleDelete = set =>
    Confirm.show(
      'Delete price options',
      `Delete the ${set.groups.length} dropdown(s) of ${packTitle(set)}?`,
      'Delete',
      'Cancel',
      async () => {
        try {
          for (const g of set.groups) {
            // eslint-disable-next-line no-await-in-loop
            await axios.delete(`${api}/${g.id}?label=${encodeURIComponent(g.label || '')}`, authHeaders);
          }
          Notify.success('Price options deleted');
        } catch (err) {
          Notify.failure(err?.response?.data?.message || 'Failed to delete');
        } finally {
          fetchGroups();
        }
      },
    );

  // Sets are reordered; the dropdowns keep their order inside a set
  const handleDragEnd = async ({source, destination}) => {
    if (!destination || destination.index === source.index) return;
    const previous = groups;
    const nextSets = [...sets];
    const [moved] = nextSets.splice(source.index, 1);
    nextSets.splice(destination.index, 0, moved);
    const next = nextSets.flatMap(set => set.groups);
    setGroups(next);
    try {
      await axios.put(
        `${api}/reorder`,
        {ids: next.map(g => g.id)},
        authHeaders,
      );
    } catch (err) {
      setGroups(previous);
      Notify.failure('Failed to reorder');
    }
  };

  // ---- Per-pack prices ----
  const openOverrides = async group => {
    setOverrideGroup(group);
    setOverridePackId('');
    setOverrideDraft({});
    try {
      const {data} = await axios.get(
        `${api}/${group.id}/overrides`,
        authHeaders,
      );
      setOverrides(data.data || []);
    } catch (err) {
      setOverrides([]);
      Notify.failure('Failed to load pack prices');
    }
  };

  const selectOverridePack = packId => {
    setOverridePackId(packId);
    // Tiers of the pack, for the original / display price preview
    loadPackTiers(packId, setPackTiers);
    const draft = {};
    overrides
      .filter(o => String(o.valuePackId) === String(packId))
      .forEach(o => {
        draft[o.itemId] = {
          type: o.type,
          percent: o.percent,
          flatBase: o.flatBase,
          flatByCountry: o.flatByCountry || {},
        };
      });
    setOverrideDraft(draft);
  };

  const saveOverrides = async () => {
    try {
      const body = {
        valuePackId: overridePackId,
        overrides: Object.entries(overrideDraft).map(([itemId, adj]) => ({
          itemId,
          ...adj,
        })),
      };
      const {data} = await axios.put(
        `${api}/${overrideGroup.id}/overrides`,
        body,
        authHeaders,
      );
      setOverrides(data.data || []);
      Notify.success(`Prices saved for ${packName(overridePackId)}`);
      fetchGroups();
    } catch (err) {
      Notify.failure(
        err?.response?.data?.message || 'Failed to save pack prices',
      );
    }
  };

  const overridePackOptions = useMemo(() => {
    if (!overrideGroup) return [];
    return overrideGroup.scope === 'specific'
      ? packs.filter(p =>
          overrideGroup.valuePackIds.map(String).includes(String(p.id)),
        )
      : packs;
  }, [overrideGroup, packs]);

  // List: one row per set of value packs (all its dropdowns together), with a
  // search by value pack and a View dialog
  const [search, setSearch] = useState('');
  const [viewSet, setViewSet] = useState(null);

  const packNamesOf = set =>
    set.scope === 'all' ? [] : (set.valuePackIds || []).map(id => packName(id));

  // Heading of a row: the value packs, not the questions
  function packTitle(set) {
    if (set.scope === 'all') return `All ${noun}s`;
    const names = packNamesOf(set);
    return names.length > 3
      ? `${names.slice(0, 3).join(', ')} +${names.length - 3} more`
      : names.join(', ');
  }

  // Dropdowns saved together share "Applies to": group them by scope + packs
  const sets = useMemo(() => {
    const byKey = new Map();
    groups.forEach(g => {
      const ids =
        g.scope === 'all' ? [] : [...(g.valuePackIds || [])].map(String).sort();
      const key = `${g.scope}:${ids.join(',')}`;
      if (!byKey.has(key)) {
        byKey.set(key, {
          key,
          scope: g.scope,
          valuePackIds: g.valuePackIds || [],
          groups: [],
        });
      }
      byKey.get(key).groups.push(g);
    });
    return [...byKey.values()];
  }, [groups]);

  const filteredSets = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sets;
    return sets.filter(set =>
      packNamesOf(set).some(n => n.toLowerCase().includes(q)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sets, packs, search]);

  const openView = set => {
    setViewSet(set);
    selectPreviewPack(
      (set.scope === 'specific' && set.valuePackIds?.[0]) || '',
    );
  };

  // The open View dialog follows list changes (e.g. after saving pack prices)
  const viewSetLive = viewSet
    ? sets.find(set => set.key === viewSet.key) || viewSet
    : null;

  const packsWithOverrides = useMemo(
    () => [...new Set(overrides.map(o => String(o.valuePackId)))],
    [overrides],
  );

  return (
    <MDBox p={3}>
      {!dialogOpen && (
        <>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={1}>
            <MDBox>
              <MDTypography variant="h6" fontWeight="medium">
                Price options
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Dropdowns on the {noun} detail page. The shopper picks an item
                in each and the price changes. A {noun} can have at most{' '}
                {MAX_DROPDOWNS_PER_PACK} dropdowns.
              </MDTypography>
            </MDBox>
            <MDButton variant="gradient" color="info" onClick={openCreate}>
              <Icon sx={{mr: 0.5}}>add</Icon> Add Price Options
            </MDButton>
          </MDBox>

          {groups.length > 0 && (
            <TextField
              fullWidth
              size="small"
              label={`Search ${noun}`}
              placeholder={`Type a ${noun} name`}
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{mb: 2}}
              InputProps={{
                startAdornment: (
                  <Icon fontSize="small" sx={{mr: 1, color: 'grey.500'}}>
                    search
                  </Icon>
                ),
              }}
            />
          )}

          {groups.length > 0 && filteredSets.length === 0 && (
            <MDBox py={3} textAlign="center">
              <MDTypography variant="button" color="text">
                No price options for &quot;{search}&quot;.
              </MDTypography>
            </MDBox>
          )}

          {groups.length === 0 ? (
            <MDBox
              py={5}
              textAlign="center"
              sx={{
                border: '1px dashed',
                borderColor: 'grey.400',
                borderRadius: 1,
              }}>
              <MDTypography variant="button" color="text">
                No price options yet.
              </MDTypography>
            </MDBox>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="price-options">
                {dropProvided => (
                  <MDBox
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}>
                    {filteredSets.map((set, index) => {
                      const isActive = set.groups.some(g => g.isActive);
                      const required = set.groups.filter(
                        g => g.isRequired,
                      ).length;
                      return (
                        <Draggable
                          key={set.key}
                          draggableId={set.key}
                          index={index}
                          isDragDisabled={!!search.trim()}>
                          {(dragProvided, snapshot) => (
                            <MDBox
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              display="flex"
                              alignItems="center"
                              gap={1.5}
                              p={1.5}
                              mb={1}
                              flexWrap="wrap"
                              sx={{
                                border: '1px solid',
                                borderColor: 'grey.300',
                                borderRadius: 1,
                                backgroundColor: snapshot.isDragging
                                  ? 'grey.100'
                                  : 'white',
                                opacity: isActive ? 1 : 0.6,
                              }}>
                              <MDBox
                                {...dragProvided.dragHandleProps}
                                display="flex"
                                sx={{
                                  cursor: search.trim() ? 'default' : 'grab',
                                }}>
                                <Icon color="action">drag_indicator</Icon>
                              </MDBox>
                              <MDBox flex="1 1 220px" minWidth={0}>
                                <MDTypography
                                  variant="button"
                                  fontWeight="medium"
                                  display="block">
                                  {packTitle(set)}
                                </MDTypography>
                                <MDTypography variant="caption" color="text">
                                  {set.groups.length} dropdown
                                  {set.groups.length === 1 ? '' : 's'}
                                  {required ? ` · ${required} required` : ''}
                                </MDTypography>
                              </MDBox>
                              {set.scope === 'all' && (
                                <Tooltip title={`All ${noun}s is turned off. Edit and pick one ${noun} to show it again.`}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label={`Not shown — pick a ${noun}`}
                                  />
                                </Tooltip>
                              )}
                              <Tooltip
                                title={
                                  isActive
                                    ? 'Active — click to hide'
                                    : 'Hidden — click to show'
                                }>
                                <Switch
                                  checked={isActive}
                                  onChange={e =>
                                    quickUpdate(set, {
                                      isActive: e.target.checked,
                                    })
                                  }
                                />
                              </Tooltip>
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => openView(set)}>
                                  <Icon fontSize="small">visibility</Icon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => openEdit(set)}>
                                  <Icon fontSize="small">edit</Icon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(set)}>
                                  <Icon fontSize="small">delete</Icon>
                                </IconButton>
                              </Tooltip>
                            </MDBox>
                          )}
                        </Draggable>
                      );
                    })}
                    {dropProvided.placeholder}
                  </MDBox>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </>
      )}

      {/* Create / edit dropdowns: shown as a page in place of the list */}
      {dialogOpen && (
        <MDBox
          borderRadius="xl"
          p={{xs: 1.5, md: 3}}
          sx={{background: '#f4f6fb'}}>
          {/* Header banner */}
          <MDBox
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            px={{xs: 2, md: 3}}
            py={2.5}
            mb={3}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap">
            <MDBox display="flex" alignItems="center" gap={1.5} minWidth={0}>
              <Tooltip title="Back to price options">
                <IconButton
                  size="small"
                  disabled={saving}
                  onClick={() => setDialogOpen(false)}
                  sx={{
                    color: 'white',
                    background: 'rgba(255,255,255,0.18)',
                    '&:hover': {background: 'rgba(255,255,255,0.3)'},
                  }}>
                  <Icon>arrow_back</Icon>
                </IconButton>
              </Tooltip>
              <MDBox minWidth={0}>
                <MDTypography variant="h5" color="white" fontWeight="medium">
                  {editing ? 'Edit price options' : 'Add price options'}
                </MDTypography>
                <MDTypography
                  variant="button"
                  color="white"
                  fontWeight="regular"
                  sx={{opacity: 0.85}}>
                  {formPackId
                    ? packName(formPackId)
                    : `Pick a ${noun} and add its dropdowns`}
                </MDTypography>
              </MDBox>
            </MDBox>
            <MDBox
              px={1.5}
              py={0.75}
              borderRadius="md"
              sx={{background: 'rgba(255,255,255,0.18)'}}>
              <MDTypography variant="button" color="white" fontWeight="bold">
                {form.dropdowns.length + savedOnPack} / {MAX_DROPDOWNS_PER_PACK}{' '}
                dropdowns
              </MDTypography>
            </MDBox>
          </MDBox>

          <Grid container spacing={3}>
            {/* Value pack + visibility */}
            <Grid item xs={12} lg={6}>
              <SectionCard
                icon="inventory_2"
                title={Noun}
                subtitle={`The ${noun} these dropdowns are shown on`}
                accent="#1d5dff">
                <ValuePackPicker
                  label={Noun}
                  fetchOptions={isProduct ? fetchProductOptions : undefined}
                  value={
                    formPackId
                      ? {id: formPackId, name: packName(formPackId)}
                      : null
                  }
                  onLoaded={rememberPacks}
                  onChange={selected => {
                    if (selected) rememberPacks([selected]);
                    setField({valuePackIds: selected ? [selected.id] : []});
                    selectPreviewPack(selected?.id || '');
                  }}
                  helperText={
                    formPackId && savedOnPack
                      ? `This pack already has ${savedOnPack} other dropdown(s). Up to ${MAX_DROPDOWNS_PER_PACK} in total.`
                      : `Up to ${MAX_DROPDOWNS_PER_PACK} dropdowns per ${noun}`
                  }
                />
                <MDBox
                  mt={2}
                  px={2}
                  py={1}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{
                    background: form.isActive ? '#ecfdf3' : '#f4f4f5',
                    border: '1px solid',
                    borderColor: form.isActive ? '#bbf7d0' : '#e4e4e7',
                  }}>
                  <MDBox>
                    <MDTypography variant="button" fontWeight="medium">
                      {form.isActive ? 'Active' : 'Hidden'}
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block">
                      {form.isActive
                        ? `Shown on the ${noun} detail page`
                        : 'Not shown to shoppers'}
                    </MDTypography>
                  </MDBox>
                  <Switch
                    checked={form.isActive}
                    onChange={e => setField({isActive: e.target.checked})}
                  />
                </MDBox>
              </SectionCard>
            </Grid>

            {/* Price preview */}
            <Grid item xs={12} lg={6}>
              <SectionCard
                icon="price_check"
                title="Price preview"
                subtitle="Prices under each item are the Low / Mid / High cards of the detail page"
                accent="#17a34a">
                <Grid container spacing={2}>
                  <PreviewControls
                    countries={countries}
                    country={previewCountry}
                    plan={previewPlan}
                    onCountryChange={changePreviewCountry}
                    onPlanChange={setPreviewPlan}
                    tiers={previewTiers}
                    duration={previewDuration}
                    onDurationChange={setPreviewDuration}
                  />
                </Grid>
              </SectionCard>
            </Grid>

            {/* One card per dropdown */}
            {form.dropdowns.map((dropdown, dIndex) => {
              const accent = DROPDOWN_ACCENTS[dIndex % DROPDOWN_ACCENTS.length];
              return (
                <Grid item xs={12} key={dropdown.id || `new-${dIndex}`}>
                  <MDBox
                    bgColor="white"
                    borderRadius="lg"
                    shadow="sm"
                    sx={{
                      border: '1px solid #e9edf5',
                      borderLeft: `4px solid ${accent}`,
                      overflow: 'hidden',
                    }}>
                    {/* Card header */}
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1.5}
                      px={{xs: 2, md: 3}}
                      py={1.5}
                      sx={{
                        background: `${accent}0d`,
                        borderBottom: '1px solid #e9edf5',
                      }}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1.5}
                        minWidth={0}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                          width={32}
                          height={32}
                          borderRadius="50%"
                          sx={{background: accent}}>
                          <MDTypography
                            variant="button"
                            color="white"
                            fontWeight="bold">
                            {dIndex + 1}
                          </MDTypography>
                        </MDBox>
                        <MDBox minWidth={0}>
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            display="block"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {dropdown.label || `Dropdown ${dIndex + 1}`}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            {dropdown.items.length} item
                            {dropdown.items.length === 1 ? '' : 's'}
                            {dropdown.isRequired
                              ? ' · Required'
                              : ' · Optional'}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      {form.dropdowns.length > 1 && (
                        <Tooltip title="Remove this dropdown">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setField({
                                dropdowns: form.dropdowns.filter(
                                  (_, i) => i !== dIndex,
                                ),
                                removedIds: dropdown.id
                                  ? [
                                      ...(form.removedIds || []),
                                      {id: dropdown.id, label: dropdown.label},
                                    ]
                                  : form.removedIds || [],
                              })
                            }>
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </MDBox>

                    <MDBox px={{xs: 2, md: 3}} py={2.5}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={7}>
                          <TextField
                            fullWidth
                            label="Dropdown label"
                            placeholder="e.g. Size of your business"
                            value={dropdown.label}
                            onChange={e =>
                              updateDropdown(
                                dIndex,
                                dropdown.keyTouched
                                  ? {label: e.target.value}
                                  : {
                                      label: e.target.value,
                                      key: slugify(e.target.value),
                                    },
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                          <TextField
                            fullWidth
                            label="Key"
                            value={dropdown.key}
                            helperText="Auto-generated from the label"
                            onChange={e =>
                              updateDropdown(dIndex, {
                                key: e.target.value,
                                keyTouched: true,
                              })
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            label="Help text (optional)"
                            value={dropdown.helpText}
                            inputProps={{maxLength: 255}}
                            onChange={e =>
                              updateDropdown(dIndex, {helpText: e.target.value})
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={dropdown.isRequired}
                                onChange={e =>
                                  updateDropdown(dIndex, {
                                    isRequired: e.target.checked,
                                  })
                                }
                              />
                            }
                            label="Required before Buy Now"
                          />
                        </Grid>
                      </Grid>

                      {/* Items */}
                      <MDBox
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mt={3}
                        mb={1.5}>
                        <Icon fontSize="small" sx={{color: accent}}>
                          list
                        </Icon>
                        <MDTypography variant="button" fontWeight="bold">
                          Items
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          — each item&apos;s price change. Several dropdowns add
                          up.
                        </MDTypography>
                      </MDBox>
                      {dropdown.items.map((item, index) => (
                        <MDBox
                          // eslint-disable-next-line react/no-array-index-key
                          key={item.id || index}
                          borderRadius="md"
                          p={1.5}
                          mb={1.5}
                          sx={{
                            background: '#f8faff',
                            border: '1px solid #e3e9f7',
                          }}>
                          <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={5}>
                              <TextField
                                fullWidth
                                size="small"
                                label={`Item ${index + 1}`}
                                placeholder="e.g. Startup"
                                value={item.label}
                                sx={{background: 'white'}}
                                onChange={e =>
                                  updateItem(dIndex, index, {
                                    label: e.target.value,
                                  })
                                }
                              />
                            </Grid>
                            <AdjustmentFields
                              value={item}
                              countries={countries}
                              onChange={next => updateItem(dIndex, index, next)}
                            />
                            <Grid
                              item
                              xs={12}
                              sm={1}
                              display="flex"
                              justifyContent="flex-end">
                              <Tooltip title="Remove item">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    updateDropdown(dIndex, {
                                      items: dropdown.items.filter(
                                        (_, i) => i !== index,
                                      ),
                                    })
                                  }>
                                  <Icon fontSize="small">delete_outline</Icon>
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </Grid>
                          {previewPackId ? (
                            <CollapsiblePrices
                              tiers={tiersOfDuration(
                                previewTiers,
                                previewDuration,
                              )}
                              plan={previewPlan}
                              adj={item}
                              countries={countries}
                            />
                          ) : (
                            <MDTypography
                              variant="caption"
                              color="text"
                              display="block"
                              mt={1}>
                              Pick the {noun} above to see the original and
                              display prices.
                            </MDTypography>
                          )}
                        </MDBox>
                      ))}
                      <MDBox
                        component="button"
                        type="button"
                        width="100%"
                        py={1.25}
                        borderRadius="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={0.75}
                        onClick={() =>
                          updateDropdown(dIndex, {
                            items: [...dropdown.items, newItem()],
                          })
                        }
                        sx={{
                          border: `1px dashed ${accent}`,
                          background: 'transparent',
                          color: accent,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontWeight: 500,
                          '&:hover': {background: `${accent}0d`},
                        }}>
                        <Icon fontSize="small">add</Icon> Add item
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Grid>
              );
            })}

            {/* Add another dropdown */}
            <Grid item xs={12}>
              {form.dropdowns.length + savedOnPack < MAX_DROPDOWNS_PER_PACK ? (
                <MDBox
                  component="button"
                  type="button"
                  width="100%"
                  py={2.5}
                  borderRadius="lg"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={0.5}
                  onClick={() =>
                    setField({dropdowns: [...form.dropdowns, newDropdown()]})
                  }
                  sx={{
                    border: '2px dashed #b7c5e8',
                    background: 'white',
                    cursor: 'pointer',
                    '&:hover': {borderColor: '#1d5dff', background: '#f5f8ff'},
                  }}>
                  <Icon sx={{color: '#1d5dff'}}>playlist_add</Icon>
                  <MDTypography variant="button" fontWeight="bold" color="info">
                    Add another dropdown
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    {MAX_DROPDOWNS_PER_PACK -
                      form.dropdowns.length -
                      savedOnPack}{' '}
                    more allowed on this {noun}
                  </MDTypography>
                </MDBox>
              ) : (
                <MDBox
                  py={1.5}
                  px={2}
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{background: '#fff7ed', border: '1px solid #fed7aa'}}>
                  <Icon fontSize="small" sx={{color: '#ea580c'}}>
                    info
                  </Icon>
                  <MDTypography variant="caption" fontWeight="medium">
                    This {noun} has the maximum of {MAX_DROPDOWNS_PER_PACK}{' '}
                    dropdowns
                    {savedOnPack ? ` (${savedOnPack} saved earlier)` : ''}.
                  </MDTypography>
                </MDBox>
              )}
            </Grid>
          </Grid>

          {/* Cancel / Save */}
          <MDBox
            bgColor="white"
            borderRadius="lg"
            shadow="sm"
            mt={3}
            px={{xs: 2, md: 3}}
            py={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1.5}
            sx={{border: '1px solid #e9edf5'}}>
            <MDTypography variant="caption" color="text">
              {form.dropdowns.length} dropdown
              {form.dropdowns.length === 1 ? '' : 's'} on this page
              {(form.removedIds || []).length
                ? ` · ${form.removedIds.length} will be deleted`
                : ''}
            </MDTypography>
            <MDBox display="flex" gap={1.5}>
              <MDButton
                variant="outlined"
                color="secondary"
                disabled={saving}
                onClick={() => setDialogOpen(false)}>
                Cancel
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                disabled={saving}
                onClick={handleSave}>
                <Icon sx={{mr: 0.5}}>save</Icon>
                {saving
                  ? 'Saving...'
                  : form.dropdowns.length > 1
                  ? `Save ${form.dropdowns.length} dropdowns`
                  : editing
                  ? 'Save changes'
                  : 'Create'}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      )}

      {/* View a set: its value packs and every dropdown (read only) */}
      <Dialog
        open={!!viewSetLive}
        onClose={() => setViewSet(null)}
        fullWidth
        maxWidth="md">
        <DialogTitle>{viewSetLive && packTitle(viewSetLive)}</DialogTitle>
        <DialogContent dividers>
          {viewSetLive && (
            <>
              {viewSetLive.scope === 'all' && (
                <MDTypography
                  variant="caption"
                  color="warning"
                  display="block"
                  mb={2}>
                  All {noun}s is turned off, so these dropdowns are not shown
                  on the site. Edit them and pick one {noun}.
                </MDTypography>
              )}

              <Grid container spacing={2} mb={2}>
                <PreviewControls
                  countries={countries}
                  country={previewCountry}
                  plan={previewPlan}
                  onCountryChange={changePreviewCountry}
                  onPlanChange={setPreviewPlan}
                  tiers={previewTiers}
                  duration={previewDuration}
                  onDurationChange={setPreviewDuration}
                />
              </Grid>

              {viewSetLive.groups.map((group, gIndex) => (
                <Paper key={group.id} variant="outlined" sx={{p: 2, mb: 2}}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                    mb={1}>
                    <MDTypography variant="button" fontWeight="medium">
                      Dropdown {gIndex + 1} — {group.label}
                    </MDTypography>
                    <MDBox display="flex" gap={0.75} alignItems="center">
                      <Chip
                        size="small"
                        color={group.isActive ? 'success' : 'default'}
                        label={group.isActive ? 'Active' : 'Hidden'}
                      />
                      <Chip
                        size="small"
                        label={group.isRequired ? 'Required' : 'Optional'}
                      />
                      <Tooltip title={`Set different prices for a specific ${noun}`}>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() => openOverrides(group)}>
                          Pack prices
                          {group.overridePackCount
                            ? ` (${group.overridePackCount})`
                            : ''}
                        </MDButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                  {group.helpText && (
                    <MDTypography
                      variant="caption"
                      color="text"
                      display="block"
                      mb={1}>
                      Help text: {group.helpText}
                    </MDTypography>
                  )}
                  {(group.items || []).map(item => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{p: 1.5, mb: 1, bgcolor: 'grey.50'}}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        gap={1}
                        flexWrap="wrap">
                        <MDTypography variant="button" fontWeight="medium">
                          {item.label}
                        </MDTypography>
                        <MDTypography variant="button" color="info">
                          {describeAdjustment(item)}
                        </MDTypography>
                      </MDBox>
                      {previewPackId && (
                        <CollapsiblePrices
                          tiers={tiersOfDuration(previewTiers, previewDuration)}
                          plan={previewPlan}
                          adj={item}
                          countries={countries}
                        />
                      )}
                    </Paper>
                  ))}
                </Paper>
              ))}
              <MDTypography variant="caption" color="text" display="block">
                Prices show the default price change. Custom prices for one pack
                are in &quot;Pack prices&quot;.
              </MDTypography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setViewSet(null)}>
            Close
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => {
              const set = viewSetLive;
              setViewSet(null);
              openEdit(set);
            }}>
            <Icon sx={{mr: 0.5}}>edit</Icon> Edit
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Per-pack prices */}
      <Dialog
        open={!!overrideGroup}
        onClose={() => setOverrideGroup(null)}
        fullWidth
        maxWidth="md">
        <DialogTitle>Pack prices — {overrideGroup?.label}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={overridePackOptions}
            getOptionLabel={option =>
              `${option.name}${
                packsWithOverrides.includes(String(option.id))
                  ? '  •  custom prices'
                  : ''
              }`
            }
            value={
              overridePackOptions.find(
                p => String(p.id) === String(overridePackId),
              ) || null
            }
            onChange={(_, selected) => selectOverridePack(selected?.id || '')}
            renderInput={params => (
              <TextField
                {...params}
                label={Noun}
                placeholder={`Search ${noun}s`}
              />
            )}
          />
          {overridePackId && (
            <Grid container spacing={2} mt={0}>
              <PreviewControls
                countries={countries}
                country={previewCountry}
                plan={previewPlan}
                onCountryChange={changePreviewCountry}
                onPlanChange={setPreviewPlan}
                tiers={packTiers}
                duration={previewDuration}
                onDurationChange={setPreviewDuration}
              />
            </Grid>
          )}

          {overridePackId &&
            (overrideGroup?.items || []).map(item => {
              const custom = overrideDraft[item.id];
              return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  sx={{p: 1.5, mt: 1.5, bgcolor: 'grey.50'}}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!custom}
                            onChange={e => {
                              const next = {...overrideDraft};
                              if (e.target.checked) {
                                next[item.id] = {
                                  type: item.type,
                                  percent: item.percent,
                                  flatBase: item.flatBase,
                                  flatByCountry: item.flatByCountry || {},
                                };
                              } else delete next[item.id];
                              setOverrideDraft(next);
                            }}
                          />
                        }
                        label={`${item.label} — default ${describeAdjustment(
                          item,
                        )}`}
                      />
                    </Grid>
                    {custom ? (
                      <AdjustmentFields
                        value={custom}
                        countries={countries}
                        onChange={next =>
                          setOverrideDraft({...overrideDraft, [item.id]: next})
                        }
                      />
                    ) : (
                      <Grid item xs={12} sm={7}>
                        <MDTypography variant="caption" color="text">
                          Uses the default price change. Tick to set a different
                          one for this pack.
                        </MDTypography>
                      </Grid>
                    )}
                  </Grid>
                  <CollapsiblePrices
                    tiers={tiersOfDuration(packTiers, previewDuration)}
                    plan={previewPlan}
                    adj={custom || item}
                    countries={countries}
                  />
                </Paper>
              );
            })}
        </DialogContent>
        <DialogActions>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={() => setOverrideGroup(null)}>
            Close
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!overridePackId}
            onClick={saveOverrides}>
            Save pack prices
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
};

PriceOptions.propTypes = {
  module: PropTypes.oneOf(['value_pack', 'product']),
};

PriceOptions.defaultProps = {
  module: 'value_pack',
};

export default PriceOptions;
